/**
 * CareTeamTab Component
 *
 * Tab content for managing care team members.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 1.4 "Care Team Tab"
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  X,
  Check,
  AlertCircle,
  Loader2,
  Trash2,
} from 'lucide-react';
import type {
  CaseFile,
  Relationship,
  CareTeamRole,
  ManageCareTeamResponse,
} from '../../types/settings';
import {
  RELATIONSHIP_LABELS,
  CARE_TEAM_ROLE_INFO,
  getRoleBadgeStyle,
} from '../../types/settings';

interface CareTeamTabProps {
  caseFile: CaseFile;
  onUpdated: () => void;
}

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (member: { name: string; email: string; relationship: Relationship; role: CareTeamRole }) => Promise<void>;
  careRecipientName: string;
  isAdding: boolean;
}

function AddMemberModal({
  isOpen,
  onClose,
  onAdd,
  careRecipientName,
  isAdding,
}: AddMemberModalProps): JSX.Element | null {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState<Relationship>('other');
  const [role, setRole] = useState<CareTeamRole>('viewer');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && email.trim()) {
      await onAdd({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        relationship,
        role,
      });
      // Reset form
      setName('');
      setEmail('');
      setRelationship('other');
      setRole('viewer');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-soft-neutral">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink">Add Team Member</h3>
              <button onClick={onClose} className="btn-ghost p-2 -mr-2">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label htmlFor="ctt-member-name" className="label">Name</label>
              <input
                id="ctt-member-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter full name"
                className="input"
                required
                disabled={isAdding}
              />
            </div>

            <div>
              <label htmlFor="ctt-member-email" className="label">Email</label>
              <input
                id="ctt-member-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="input"
                required
                disabled={isAdding}
              />
            </div>

            <div>
              <label htmlFor="ctt-member-relationship" className="label">Relationship to {careRecipientName}</label>
              <select
                id="ctt-member-relationship"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value as Relationship)}
                className="input"
                disabled={isAdding}
              >
                {(Object.entries(RELATIONSHIP_LABELS) as [Relationship, string][])
                  .filter(([key]) => key !== 'self')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="label">Role</label>
              <div className="space-y-2">
                {(
                  Object.entries(CARE_TEAM_ROLE_INFO) as [
                    CareTeamRole,
                    { label: string; description: string }
                  ][]
                )
                  .filter(([key]) => key !== 'contact')
                  .map(([value, info]) => (
                    <label
                      key={value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        role === value
                          ? 'border-evergreen bg-evergreen/5'
                          : 'border-soft-neutral hover:border-muted'
                      } ${isAdding ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={value}
                        checked={role === value}
                        onChange={() => setRole(value)}
                        className="mt-0.5"
                        disabled={isAdding}
                      />
                      <div>
                        <p className="text-sm font-medium text-ink">{info.label}</p>
                        <p className="text-xs text-muted">{info.description}</p>
                      </div>
                    </label>
                  ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-soft-neutral">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={isAdding}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={isAdding || !name.trim() || !email.trim()}
              >
                {isAdding ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Team Member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export function CareTeamTab({ caseFile, onUpdated }: CareTeamTabProps): JSX.Element {
  const { user } = useAuth();

  // UI state
  const [showAddMember, setShowAddMember] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const careTeam = caseFile.careTeam || [];

  // Get initials for avatar
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar background color based on role
  const getAvatarBg = (role?: string): string => {
    switch (role) {
      case 'owner':
        return 'bg-evergreen';
      case 'admin':
        return 'bg-slate-ink';
      case 'editor':
        return 'bg-muted';
      default:
        return 'bg-gray-400';
    }
  };

  const handleAddMember = async (member: {
    name: string;
    email: string;
    relationship: Relationship;
    role: CareTeamRole;
  }) => {
    setIsAdding(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the createCareTeamInvitation function
      const createInvitation = httpsCallable<
        {
          caseFileId: string;
          inviteeEmail: string;
          inviteeName?: string;
          relationship: string;
          role: string;
        },
        {
          success: boolean;
          invitationCode?: string;
          message?: string;
        }
      >(functions, 'createCareTeamInvitation');

      const result = await createInvitation({
        caseFileId: caseFile.caseFileId,
        inviteeEmail: member.email,
        inviteeName: member.name,
        relationship: member.relationship,
        role: member.role,
      });

      if (result.data.success) {
        setSuccess(`Invitation sent to ${member.email}. They can join using code: ${result.data.invitationCode}`);
        setShowAddMember(false);
        onUpdated();
        setTimeout(() => setSuccess(null), 10000);
      } else {
        setError(result.data.message || 'Failed to send invitation');
      }
    } catch (err: any) {
      console.error('Error adding care team member:', err);
      setError(err?.message || 'Failed to add team member');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from the care team?`)) return;

    setIsRemoving(userId);
    setError(null);
    setSuccess(null);

    try {
      const manageCareTeam = httpsCallable<
        { caseFileId: string; operation: 'remove'; userId: string },
        ManageCareTeamResponse
      >(functions, 'manageCareTeam');

      const result = await manageCareTeam({
        caseFileId: caseFile.caseFileId,
        operation: 'remove',
        userId,
      });

      if (result.data.success) {
        setSuccess(`${memberName} has been removed from the care team`);
        onUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.data.error || 'Failed to remove team member');
      }
    } catch (err: any) {
      console.error('Error removing care team member:', err);
      setError(err?.message || 'Failed to remove team member');
    } finally {
      setIsRemoving(null);
    }
  };

  const formatRelationship = (rel: string): string => {
    return RELATIONSHIP_LABELS[rel as Relationship] || rel;
  };

  const formatRole = (role?: string): string => {
    if (!role) return 'Unknown';
    const info = CARE_TEAM_ROLE_INFO[role as CareTeamRole];
    return info?.label || (role.charAt(0).toUpperCase() + role.slice(1));
  };

  return (
    <>
      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success mb-6 flex items-center gap-2" role="status" aria-live="polite">
          <Check className="w-5 h-5" aria-hidden="true" />
          {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error mb-6 flex items-center gap-2" role="alert">
          <AlertCircle className="w-5 h-5" aria-hidden="true" />
          {error}
        </div>
      )}

      {/* Care Team Members Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-ink mb-1">Care Team Members</h3>
              <p className="text-sm text-muted">
                People who help manage {caseFile.name}'s care
              </p>
            </div>
            <button
              onClick={() => setShowAddMember(true)}
              disabled={caseFile.isLocked}
              className={`btn-secondary flex items-center gap-2 ${
                caseFile.isLocked ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Plus className="w-4 h-4" />
              Add Team Member
            </button>
          </div>

          {/* Locked Warning */}
          {caseFile.isLocked && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">CaseFile is Locked</p>
                <p className="text-xs text-amber-700 mt-1">
                  Care team cannot be modified until the CaseFile is unlocked.
                </p>
              </div>
            </div>
          )}

          {/* Team Members List */}
          {careTeam.length > 0 ? (
            <div className="space-y-2">
              {careTeam.map((member, index) => {
                const isOwner = member.role === 'owner' || member.userId === caseFile.ownerId;
                const isCurrentUser = member.userId === user?.uid;
                const roleStyle = getRoleBadgeStyle(isOwner ? 'owner' : member.role);

                return (
                  <div
                    key={member.userId || index}
                    className="flex items-center justify-between p-4 bg-airy rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${getAvatarBg(
                          isOwner ? 'owner' : member.role
                        )}`}
                      >
                        {getInitials(member.name || 'U')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-ink">
                          {member.name}
                          {isCurrentUser && (
                            <span className="text-xs text-muted ml-2">(You)</span>
                          )}
                        </h4>
                        <p className="text-sm text-muted">
                          {member.email}
                          {member.relationship && ` \u2022 ${formatRelationship(member.relationship)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${roleStyle.bg} ${roleStyle.text}`}
                      >
                        {isOwner ? 'Owner' : formatRole(member.role)}
                      </span>
                      {!isOwner && !caseFile.isLocked && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              member.userId && handleRemoveMember(member.userId, member.name)
                            }
                            disabled={isRemoving === member.userId}
                            className="btn-ghost p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                            aria-label="Remove from care team"
                          >
                            {isRemoving === member.userId ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 bg-airy rounded-lg text-center">
              <p className="text-muted">No care team members yet.</p>
              <button
                onClick={() => setShowAddMember(true)}
                disabled={caseFile.isLocked}
                className="text-sm text-evergreen hover:underline mt-2"
              >
                Invite someone to help
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
        careRecipientName={caseFile.name}
        isAdding={isAdding}
      />
    </>
  );
}

export default CareTeamTab;
